import { Injectable } from '@angular/core';
import { APP_ID, DEVICE_ID_KEY } from '@app/configs';

/**
 * Serwis do zarządzania identyfikatorem urządzenia (deviceId).
 * 
 * deviceId jest generowany raz i przechowywany w localStorage.
 * Jest wymagany przez backend przy logowaniu i odświeżaniu tokenów.
 * Umożliwia zarządzanie sesjami per urządzenie.
 * 
 * MULTI-APP SUPPORT:
 * Każda aplikacja frontendowa (panel, główna app, mobile) ma osobny deviceId
 * z prefixem APP_ID. Dzięki temu użytkownik może być zalogowany
 * jednocześnie do wielu aplikacji bez konfliktów sesji.
 * 
 * Przykład:
 * - panel.test.pl: deviceId = "panel_abc123..."
 * - test.pl: deviceId = "app_def456..."
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private _deviceId: string | null = null;

  constructor() {
    this._loadDeviceId();
  }

  /**
   * Pobiera deviceId. Jeśli nie istnieje, generuje nowy.
   * deviceId zawiera prefix aplikacji (APP_ID) dla rozróżnienia sesji.
   */
  getDeviceId(): string {
    if (!this._deviceId) {
      this._deviceId = this._generateDeviceId();
      this._saveDeviceId(this._deviceId);
    }
    return this._deviceId;
  }

  /**
   * Resetuje deviceId (np. po wylogowaniu ze wszystkich urządzeń)
   */
  resetDeviceId(): string {
    this._deviceId = this._generateDeviceId();
    this._saveDeviceId(this._deviceId);
    return this._deviceId;
  }

  /**
   * Pobiera sam UUID bez prefixu (przydatne do debugowania)
   */
  getDeviceUuid(): string {
    const deviceId = this.getDeviceId();
    const prefix = `${APP_ID}_`;
    return deviceId.startsWith(prefix) ? deviceId.slice(prefix.length) : deviceId;
  }

  /**
   * Pobiera identyfikator aplikacji
   */
  getAppId(): string {
    return APP_ID;
  }

  /**
   * Generuje nowy unikalny deviceId z prefixem aplikacji.
   * Format: "{APP_ID}_{UUID}" np. "panel_abc123-def456-..."
   */
  private _generateDeviceId(): string {
    const uuid = this._generateUuid();
    return `${APP_ID}_${uuid}`;
  }

  /**
   * Generuje UUID v4.
   * Używa crypto.randomUUID() jeśli dostępne, w przeciwnym razie fallback.
   */
  private _generateUuid(): string {
    // Nowoczesne przeglądarki (Chrome 92+, Firefox 95+, Safari 15.4+)
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    
    // Fallback dla starszych przeglądarek
    return this._generateUuidFallback();
  }

  /**
   * Fallback generowania UUID v4 dla starszych przeglądarek
   */
  private _generateUuidFallback(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Ładuje deviceId z localStorage
   */
  private _loadDeviceId(): void {
    try {
      const storedId = localStorage.getItem(DEVICE_ID_KEY);
      if (storedId && this._isValidDeviceId(storedId)) {
        this._deviceId = storedId;
      }
    } catch (error) {
      console.warn('[DeviceService] Failed to load deviceId from localStorage:', error);
    }
  }

  /**
   * Zapisuje deviceId do localStorage
   */
  private _saveDeviceId(deviceId: string): void {
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    } catch (error) {
      console.warn('[DeviceService] Failed to save deviceId to localStorage:', error);
    }
  }

  /**
   * Sprawdza czy deviceId jest poprawny (ma prefix i UUID)
   */
  private _isValidDeviceId(deviceId: string): boolean {
    // Sprawdzamy czy zaczyna się od aktualnego APP_ID
    const prefix = `${APP_ID}_`;
    if (!deviceId.startsWith(prefix)) {
      return false;
    }
    
    // Sprawdzamy czy UUID jest poprawny
    const uuid = deviceId.slice(prefix.length);
    return this._isValidUuid(uuid);
  }

  /**
   * Sprawdza czy string jest poprawnym UUID
   */
  private _isValidUuid(str: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  }
}
